import { messageQueryKey } from "@apis:message/message";
import { useMutationSearch } from "@apis:search/search";
import { BrandedLogo } from "@components:BrandedLogo";
import { Header } from "@components:Header";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useStreamAI } from "@hooks:useStreamAI";
import { BotSelectionPersistent } from "@modules:BotSelection/BotSelection";
import { alpha, Box, useTheme } from "@mui/material";
import { SearchBox } from "@pages:app/talk/Search/SearchBox";
import { AIResponse } from "@pages:app/talk/shared/AIResponse";
import { Reference } from "@pages:app/talk/shared/Reference";
import { useSearchActions, useSearchData } from "@stores:app/talk/search";
import { useToasterStoreActions } from "@stores:global";
import { isEmpty, noop } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppType } from "@:constants";

export const Search = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { answer, question, references, llmQuestion } = useSearchData();

  const { setAnswer, setQuestion, setReferences, reset, setLLMQuestion } =
    useSearchActions();

  const [showReferences, setShowReferences] = useState(false);

  const { selectedBotId } = useBotSelection();

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const toastActions = useToasterStoreActions();

  const invalidateQueries = useInvalidateQueries();

  // Note: now, we can only use stream
  const isBotStreaming = true;

  const askAIMutation = useMutationSearch({
    onSuccess: async (data) => {
      if (question === "") {
        return;
      }
      setAnswer(data.answer);
      setReferences(data.docs);
    },
  });

  const { askAIStream, cancel } = useStreamAI({
    setReferences: setReferences,
    setAnswer: setAnswer,
  });

  const isSearchBtnDisabled = question.length === 0 || !selectedBotId;

  const isHideAIResponse = isEmpty(answer);

  const handleAskAI = async (question?: string) => {
    if (!question) {
      return;
    }
    await cancel();
    setAnswer("");
    setReferences([]);
    setLLMQuestion("");
    await toastActions
      .setToasterPromise(
        isBotStreaming
          ? askAIStream({
              question,
              botId: selectedBotId as string,
            })
          : askAIMutation
              .mutateAsync({
                question,
                botId: selectedBotId as string,
              })
              .then(() => noop),
        {
          onPending: () => ({
            message: t("talk.searchThinking"),
          }),
          onError: () => ({
            message: t("talk.searchFailed"),
          }),
        },
      )
      .finally(async () => {
        await invalidateQueries.run([
          {
            queryKey: messageQueryKey.getAll.queryKey,
          },
        ]);
      });
  };

  const handleKeyPress = async (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await handleAskAI(question);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "30px",
        background: (theme) => theme.palette.common.white,
        flex: 1,
        height: "100%",
      }}
    >
      <Header
        sx={{
          background: (theme) => theme.palette.common.white,
          borderTopLeftRadius: "30px",
          borderTopRightRadius: "30px",
        }}
        rightSide={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={{ paddingX: "0.5rem" }}>
              <BotSelectionPersistent app={AppType.Search} />
            </Box>
            <SwitchPage />
          </Box>
        }
      />

      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "0.5rem",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "0.25rem",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0,0,0,0.3)",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "5rem",
          }}
        >
          <BrandedLogo />
          <Box
            sx={{
              marginTop: "2rem",
              width: "100%",
            }}
          >
            <SearchBox
              question={question}
              setQuestion={setQuestion}
              onKeyDown={handleKeyPress}
              onSearch={handleAskAI}
              isSearchDisable={isSearchBtnDisabled}
            />
          </Box>
          {!isHideAIResponse && (
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: "30px",
                padding: "1rem 2rem",
                borderBottomLeftRadius: showReferences ? "0" : "30px",
                width: "90%",
                marginTop: "5rem",
              }}
            >
              <AIResponse
                answer={answer}
                references={references}
                setShowReferences={setShowReferences}
                showReferences={showReferences}
                llmQuestion={llmQuestion}
              />
            </Box>
          )}
          {showReferences && !isHideAIResponse && (
            <Box
              sx={{
                width: "90%",
                marginY: "1rem",
                padding: "1rem ",
                borderRadius: "30px",
                borderTopLeftRadius: "0",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
                boxShadow: "0px 8px 24px 0px rgba(0,0,0,0.25)",
              }}
            >
              <Reference references={references} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
